import * as Flows from './types.no-deps'
import { assumeExhaustion } from '../../lib/typeHelpers.no-deps'
import { pipe } from '../../lib/functional/pipe.no-deps'
import { compact, filter, flatten, map, mapField, uniq } from '../../lib/functional/array.no-deps'
import type { ReportClientId } from '../reports/types.no-deps'
import type { FileId } from '../files/types.no-deps'
import { toLoopPath, type LoopPathIds } from './LoopPath.no-deps'
import { differenceBy } from 'lodash'
import type { ElementId } from './types.no-deps'

export function sortChronologicallyDescending(answers: Flows.Answer[]) {
    return answers.slice(0).sort((a, b) => +new Date(b.timestamp) - +new Date(a.timestamp))
}

export function findAnswer(answers: Flows.Answer[], elementId: Flows.ElementId, loopPathIds: LoopPathIds) {
    return answers.find(isAnswerPredicate(elementId, loopPathIds))
}

export function isAnswerPredicate(elementId: Flows.ElementId, loopPathIds: LoopPathIds) {
    const loopPath = toLoopPath(loopPathIds)
    return (answer: Flows.Answer) => answer.element === elementId && answer.loopPath === loopPath
}

export function getAnswerLoopPath(answer: Flows.Answer) {
    return answer?.loopPath ?? toLoopPath([])
}

export function getAnswerWithSubtaskReport(answers: Flows.Answer[], reportId: Flows.ReportId) {
    return answers.find((a) => a.type === 'reports' && a.value.includes(reportId))
}

export function getConflicts(answers: Flows.Answer[]) {
    const conflicts: Flows.SectionSummary['conflicts'] = []

    // Create a map to group answers by element and loopPath
    const groupedAnswers = new Map<string, Flows.Answer[]>()

    for (const answer of answers) {
        const key = answer.element + getAnswerLoopPath(answer)
        if (!groupedAnswers.has(key)) {
            groupedAnswers.set(key, [])
        }
        groupedAnswers.get(key)!.push(answer)
    }

    // Iterate over grouped answers to find conflicts
    for (const elementAnswers of groupedAnswers.values()) {
        for (let i = 0; i < elementAnswers.length - 1; i++) {
            const a = elementAnswers[i]
            const b = elementAnswers[i + 1]
            if (a.oldAnswer !== b.id) {
                conflicts.push([a.id, b.id])
            }
        }
    }

    return conflicts
}

export const isUnanswered = (answer: Flows.Answer) => answer?.value == null

export function answerIsEmpty(answer: Flows.Answer) {
    return currentAnswerIsEmpty(answer)
}

export function currentAnswerIsEmpty(answer: Flows.Answer) {
    if (isUnanswered(answer)) {
        return true
    }

    switch (answer.type) {
        case 'checkbox':
        case 'dropdown':
        case 'photo':
        case 'image-select':
        case 'files-select':
        case 'extensible-files':
        case 'reports':
            return !answer.value.length

        case 'yes-no':
        case 'radio':
        case 'number':
        case 'confirmation':
        case 'signature':
        case 'section':
        case 'datetime':
        case 'guide':
            return false

        case 'text-capture':
            return answer.value === ''

        case 'unit-select':
            return answer.value !== 'no-units-available' && !answer.value.length

        default:
            assumeExhaustion(answer)
            return false
    }
}

export type CreateAnswerBase = (
    element: ElementId,
    loopPathIds: LoopPathIds,
    oldAnswer?: Flows.Answer | null,
    reportId?: Flows.ReportId,
    dynamicElement?: boolean
) => Flows.AnswerBase

export type CreateAnswer<Answer extends Flows.AnswerBase> = (fn: (base: Flows.AnswerBase) => Answer) => Promise<void>

interface CreateAnswerArgs {
    reportId: Flows.ReportId
    userId: number
    createUUID: () => string
    getCoordinates: () => { latitude: number; longitude: number }
    elementId: ElementId
    loopPathIds: LoopPathIds
    oldAnswer?: Flows.Answer | null
    dynamicElement?: boolean
}

export function createAnswerBase(args: CreateAnswerArgs): Flows.AnswerBase {
    const { latitude, longitude } = args.getCoordinates()
    return {
        id: args.createUUID() as Flows.AnswerId,
        element: args.elementId,
        loopPath: toLoopPath(args.loopPathIds),
        latitude,
        longitude,
        report: args.reportId,
        timestamp: new Date(),
        user: args.userId,
        oldAnswer: args.oldAnswer != null ? args.oldAnswer.id : null,
        dynamicElement: args.dynamicElement,
    }
}

export function getAnswerType(elementType: Flows.Element['type']): Flows.Answer['type'] {
    switch (elementType) {
        case 'confirmation':
        case 'signature':
        case 'section':
        case 'datetime':
        case 'dropdown':
        case 'files-select':
        case 'unit-select':
        case 'yes-no':
        case 'number':
        case 'radio':
        case 'checkbox':
        case 'image-select':
        case 'text-capture':
        case 'guide':
            return elementType
        case 'flow':
            return 'reports'
        case 'calculation':
            return 'number'
        case 'photo':
            return 'extensible-files'
        case 'image-content':
        case 'text-content':
        case 'video-content':
        case 'link':
        case 'loop':
            return null

        default:
            assumeExhaustion(elementType)
            throw new Error('Unregcognized element type')
    }
}

export function getSubtaskReportsInAnswers(answers: Flows.Answer[]): ReportClientId[] {
    return pipe(answers, filter(Flows.isReportsAnswer), mapField('value'), flatten, uniq)
}

export function isDefaultGeneratedAnswer(answer: Flows.Answer): boolean {
    return (answer.type === 'datetime' || answer.type === 'unit-select') && answer?.automaticallyGenerated
}

/**
 * This is used to remove the update state of answers before being uploaded because the server
 * does not need to know about the upload status of answers
 */
export function omitExtraAnswerFields(flowAnswer: Flows.Answer): Flows.Answer {
    const pick = <T extends Flows.AnswerType, V>(answer: Flows._Answer<T, V>): Flows._Answer<T, V> => ({
        type: answer.type,
        id: answer.id,
        element: answer.element,
        latitude: answer.latitude,
        longitude: answer.longitude,
        report: answer.report,
        timestamp: answer.timestamp,
        user: answer.user,
        value: answer.value,
        oldAnswer: answer.oldAnswer,
        automaticallyGenerated: answer.automaticallyGenerated,
        loopPath: answer.loopPath,
        dynamicElement: answer.dynamicElement,
    })

    switch (flowAnswer.type) {
        case 'confirmation':
            return pick(flowAnswer)
        case 'checkbox':
            return pick(flowAnswer)
        case 'datetime':
            return pick(flowAnswer)
        case 'dropdown':
            return pick(flowAnswer)
        case 'extensible-files':
            return pick(flowAnswer)
        case 'files-select':
            return pick(flowAnswer)
        case 'image-select':
            return pick(flowAnswer)
        case 'number':
            return pick(flowAnswer)
        case 'photo':
            return pick(flowAnswer)
        case 'radio':
            return pick(flowAnswer)
        case 'reports':
            return pick(flowAnswer)
        case 'section':
            return pick(flowAnswer)
        case 'signature':
            return pick(flowAnswer)
        case 'text-capture':
            return pick(flowAnswer)
        case 'unit-select':
            return pick(flowAnswer)
        case 'yes-no':
            return pick(flowAnswer)
        case 'guide':
            return pick(flowAnswer)
        default:
            assumeExhaustion(flowAnswer)
            return flowAnswer
    }
}

export function getFileIdsFromAnswers(answers: Flows.Answer[]) {
    const fileIds = new Set<FileId>()

    for (const answer of answers) {
        if (currentAnswerIsEmpty(answer)) continue

        switch (answer.type) {
            case 'confirmation':
            case 'checkbox':
            case 'datetime':
            case 'dropdown':
            case 'image-select':
            case 'number':
            case 'radio':
            case 'reports':
            case 'section':
            case 'text-capture':
            case 'unit-select':
            case 'yes-no':
            case 'guide':
                continue
            case 'extensible-files':
                for (const value of answer.value) {
                    if (value.type === 'PhotoFile') {
                        fileIds.add(value.photo)
                        if (value.annotation != null) fileIds.add(value.annotation)
                    }
                    if (value.type === 'VideoFile') {
                        fileIds.add(value.video)
                    }
                }
                continue
            case 'files-select':
                for (const fileId of answer.value) {
                    fileIds.add(fileId)
                }
                continue
            case 'photo':
                for (const fileName of answer.value) {
                    fileIds.add(Flows.photoFileNameToFileId(fileName))
                }
                continue
            case 'signature':
                fileIds.add(Flows.photoFileNameToFileId(answer.value))
                continue
            default:
                assumeExhaustion(answer)
        }
    }

    return [...fileIds]
}

export function getFileIdsForFileSelectFromAnswers(answers: Flows.Answer[]) {
    const fileSelectAnswersIds = uniq(
        pipe(
            answers,
            filter((answer) => answer.type === 'files-select'),
            map((answer) => answer.value as FileId[]),
            flatten
        ) as FileId[]
    )
    return fileSelectAnswersIds
}

export function compactAnswerValue(flowAnswer: Flows.Answer): Flows.Answer {
    switch (flowAnswer.type) {
        case 'confirmation':
            return flowAnswer
        case 'checkbox':
            return { ...flowAnswer, value: flowAnswer.value === null ? [] : compact(flowAnswer.value) }
        case 'datetime':
            return flowAnswer
        case 'dropdown':
            return { ...flowAnswer, value: flowAnswer.value === null ? [] : compact(flowAnswer.value) }
        case 'extensible-files':
            return { ...flowAnswer, value: flowAnswer.value === null ? [] : compact(flowAnswer.value) }
        case 'files-select':
            return { ...flowAnswer, value: flowAnswer.value === null ? [] : compact(flowAnswer.value) }
        case 'image-select':
            return { ...flowAnswer, value: flowAnswer.value === null ? [] : compact(flowAnswer.value) }
        case 'number':
            return flowAnswer
        case 'photo':
            return { ...flowAnswer, value: flowAnswer.value === null ? [] : compact(flowAnswer.value) }
        case 'radio':
            return flowAnswer
        case 'reports':
            return { ...flowAnswer, value: flowAnswer.value === null ? [] : compact(flowAnswer.value) }
        case 'section':
            return flowAnswer
        case 'signature':
            return flowAnswer
        case 'text-capture':
            return flowAnswer
        case 'unit-select':
            return flowAnswer.value === 'no-units-available'
                ? flowAnswer
                : { ...flowAnswer, value: flowAnswer.value === null ? [] : compact(flowAnswer.value) }
        case 'yes-no':
            return flowAnswer
        case 'guide':
            return {
                ...flowAnswer,
                value: Array.isArray(flowAnswer.value) ? compact(flowAnswer.value) : flowAnswer.value,
            }
        default:
            assumeExhaustion(flowAnswer)
            return flowAnswer
    }
}

export function areAnswersDifferent(as: Flows.Answer[], bs: Flows.Answer[]) {
    return differenceBy(as, bs, (a) => a.id).length > 0 || differenceBy(bs, as, (a) => a.id).length > 0
}
